import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {CATS, SVC_L, SVC_ICONS, hubLang} from '../../lib/curo-services.js';

/**
 * 큐브로이드 서비스 런처 — Studio에서 다른 학습 도구(허브 16개 플랫폼)로 바로 이동.
 * 통합 전략 첫 걸음(2026-07-22): 코드베이스 합치기 없이 진입점만 통합 → 교차 사용↑, 작은 서비스 트래픽 유입(사장 방지).
 * 자체 상태(open) + 커스텀 이벤트(curoai-open-services)로 열림. 데이터·아이콘·다국어는 lib/curo-services(허브 포팅).
 * 각 서비스는 새 탭. 현재(큐로 AI)=표시만, soon(개발중)=비활성.
 */

const CLOSE_SVG = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
const EXT_SVG = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6M20 4l-8 8M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5"/></svg>';

class ServicesModal extends React.Component {
    constructor (props) {
        super(props);
        this.state = {open: false};
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
    }
    componentDidMount () { window.addEventListener('curoai-open-services', this.open); }
    componentWillUnmount () { window.removeEventListener('curoai-open-services', this.open); }
    open () { this.setState({open: true}); }
    close () { this.setState({open: false}); }
    _loc () {
        return hubLang((this.props.intl && this.props.intl.locale) || 'en');
    }
    renderCard (svc, loc, L, key) {
        const name = svc.n[loc] || svc.n.en;
        const desc = svc.d[loc] || svc.d.en;
        const grades = (svc.grades || []).map(g => (L.grade && L.grade[g]) || g);
        const inner = (
            <React.Fragment>
                <span style={S.icon} dangerouslySetInnerHTML={{__html: SVC_ICONS[svc.icon] || ''}} />
                <div style={{minWidth: 0, flex: 1}}>
                    <div style={S.name}>
                        <span style={S.nameText}>{name}</span>
                        {svc.current ? <span style={S.badgeCur}>{L.current}</span> : null}
                        {svc.soon ? <span style={S.badgeSoon}>{L.soon}</span> : null}
                        {(!svc.current && !svc.soon) ? <span style={S.ext} dangerouslySetInnerHTML={{__html: EXT_SVG}} /> : null}
                    </div>
                    <div style={S.desc}>{desc}</div>
                    <div>{grades.map((g, i) => <span key={i} style={S.chip}>{g}</span>)}</div>
                </div>
            </React.Fragment>
        );
        if (svc.current) return <div key={key} style={Object.assign({}, S.card, S.cardCurrent)}>{inner}</div>;
        if (svc.soon || !svc.url) return <div key={key} style={Object.assign({}, S.card, S.cardSoon)}>{inner}</div>;
        return (
            <a key={key} style={S.card} href={svc.url} target="_blank" rel="noopener noreferrer">{inner}</a>
        );
    }
    render () {
        if (!this.state.open) return null;
        const loc = this._loc();
        const L = SVC_L[loc] || SVC_L.en;
        return (
            <div style={S.overlay} onClick={this.close}>
                <div style={S.modal} onClick={e => e.stopPropagation()}>
                    <div style={S.head}>
                        <div>
                            <div style={S.title}>{L.title}</div>
                            <div style={S.sub}>{L.sub}</div>
                        </div>
                        <button style={S.closeBtn} onClick={this.close} aria-label="Close">
                            <span style={{display: 'inline-flex', fontSize: 18}} dangerouslySetInnerHTML={{__html: CLOSE_SVG}} />
                        </button>
                    </div>
                    <div style={S.body}>
                        {CATS.map(cat => (
                            <div key={cat.key} style={{marginTop: 14}}>
                                <div style={S.catRow}>
                                    <span style={Object.assign({}, S.dot, {background: cat.color})} />
                                    <span style={S.catLabel}>{L.cat[cat.key]}</span>
                                </div>
                                <div style={S.grid}>
                                    {cat.services.map((svc, i) => this.renderCard(svc, loc, L, `${cat.key}${i}`))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
}

const S = {
    overlay: {position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999},
    modal: {background: '#fff', borderRadius: 14, width: 'min(640px, 95vw)', maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 12px 40px rgba(0,0,0,0.3)', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    head: {display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: '16px 20px 4px'},
    title: {fontSize: 18, fontWeight: 'bold', color: '#1f2540'},
    sub: {fontSize: 12, color: '#67708a', marginTop: 2},
    closeBtn: {border: 'none', background: 'transparent', color: '#9aa1b4', cursor: 'pointer', padding: 4, lineHeight: 1},
    body: {padding: '4px 20px 20px'},
    catRow: {display: 'flex', alignItems: 'center', gap: 7, margin: '2px 0 8px'},
    dot: {width: 10, height: 10, borderRadius: 3, flex: 'none'},
    catLabel: {fontSize: 13, fontWeight: 'bold', color: '#4a5169'},
    grid: {display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 8},
    card: {display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', border: '1px solid #e6e8f0',
        borderRadius: 12, background: '#fff', textDecoration: 'none', color: 'inherit'},
    cardCurrent: {border: '2px solid #4c97ff', background: '#f3f8ff'},
    cardSoon: {opacity: 0.62, cursor: 'default'},
    icon: {width: 40, height: 40, flex: 'none', display: 'inline-flex'},
    name: {fontSize: 14, fontWeight: 500, color: '#1f2540', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap'},
    nameText: {color: '#1f2540'},
    ext: {display: 'inline-flex', fontSize: 12, color: '#b3b9c9'},
    desc: {fontSize: 12, color: '#67708a', margin: '2px 0 5px', lineHeight: 1.4},
    chip: {fontSize: 11, color: '#5f6880', background: '#f1f3f9', borderRadius: 999, padding: '1px 7px', margin: '0 3px 3px 0', display: 'inline-block'},
    badgeCur: {fontSize: 11, color: '#2f6fd0', background: '#dcebff', borderRadius: 999, padding: '1px 8px'},
    badgeSoon: {fontSize: 11, color: '#8a6d1a', background: '#fbeecb', borderRadius: 999, padding: '1px 8px'}
};

ServicesModal.propTypes = {
    intl: PropTypes.object
};

export default injectIntl(ServicesModal);
